import React, { useEffect, useState, useRef  } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';

import imageCompression from 'browser-image-compression';
import { Link } from 'react-router-dom';

import ImageWithLoader from './components/ImageWithLoader';

function Gallery() {
  
  const { navigate, isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const queryClient = useQueryClient();
  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

 
// swipe & image zooming
const [selectedIndex, setSelectedIndex] = useState(null);
const [gallery, setGallery] = useState([]);
const [scale, setScale] = useState(1);
const [offset, setOffset] = useState({ x: 0, y: 0 });
const [dragging, setDragging] = useState(false);
const [startX, setStartX] = useState(null);
const [endX, setEndX] = useState(null);
const [lastTouchDistance, setLastTouchDistance] = useState(null);
const lastPos = useRef({ x: 0, y: 0 });
  

  //display albums

  const { data: album, isLoading, isError, refetch } = useQuery({
    queryKey: ['familyGallery'],
    queryFn: () =>
      axios.get(`${API_URL}/display-album-gallery-api`, {
        withCredentials: true,
      }).then((res) => {
        if (res.data.Status === "Success") return res.data.listings;
        throw new Error("Failed to fetch album");
      }),
    staleTime: 0,
    cacheTime: Infinity,
  });


  useEffect(() => {
    const handleKey = (e) => {
      if (selectedIndex === null || !gallery.length) return;
      if (e.key === "ArrowRight") {
        setSelectedIndex((prev) => (prev + 1) % gallery.length);
      } else if (e.key === "ArrowLeft") {
        setSelectedIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
      } else if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedIndex, gallery]);



    //create album
    const [listUploadModalOpen, setListUploadModalOpen] = useState(false);
  const [isUploadingEdit, setIsUploadingEdit] = useState(false);

const [formData, setFormData] = useState({
  album_name: '',
});

const handleChange = (e) => {
  setFormData({ ...formData, [e.target.name]: e.target.value });
};


const [isSubmitting, setIsSubmitting] = useState(false);

const handleSubmitAlbum = async () => {
  if (!formData.album_name.trim()) {
    toast.error("Album name is required", { position: "top-right", autoClose: 3000 });
    return;
  }

  setIsSubmitting(true); // disable button
  try {
    const res = await axios.post(`${API_URL}/create-album-gallery-api`, formData, {
      withCredentials: true,
    });

    toast.success("Created successfully", { position: "top-right", autoClose: 3000 });
    queryClient.invalidateQueries(["familyGallery"]);
    setListUploadModalOpen(false);
    setFormData({ album_name: "" }); // reset form
  } catch (error) {
    toast.error("Something went wrong", { position: "top-right", autoClose: 3000 });
  } finally {
    setIsSubmitting(false); // re-enable button
  }
};


//edit album name

const [isEditModalOpen, setEditModalOpen] = useState(false);
const [editedAlbum, setEditedAlbum] = useState({ id: null, name: '' });

const [isUpdating, setIsUpdating] = useState(false);

const handleUpdateAlbum = async () => {

  if (!editedAlbum.name || !editedAlbum.name.trim()) {
    toast.error("Album name is required", { position: "top-right", autoClose: 3000 });
    return;
  }

  setIsUpdating(true); // Start loading
  try {
    const res = await axios.post(`${API_URL}/update-album-name`, {
      album_id: editedAlbum.id,
      album_name: editedAlbum.name,
    });

    if (res.data.Status === 'Success') {
      toast.success('Album name updated');
      setEditModalOpen(false);
      refetch(); // reload albums
    } else {
      toast.error('Failed to update album');
    }
  } catch (err) {
    console.error(err);
    toast.error('Server error');
  } finally {
    setIsUpdating(false); // Stop loading
  }
};


//delete album
const handleDeleteAlbum = async () => {
  setIsUpdating(true); // Start loading
  try {
    const res = await axios.post(`${API_URL}/delete-album-api`, {
      album_id: editedAlbum.id,
    });

    if (res.data.Status === 'Success') {
      toast.success('Album deleted');
      setEditModalOpen(false);
      refetch(); // reload albums
    } else {
      toast.error('Failed to delete album');
    }
  } catch (err) {
    if (err.response?.data?.Status === 'Blocked') {
      toast.error(err.response.data.Message);
    } else {
      console.error(err);
      toast.error('Server error');
    }
  } finally {
    setIsUpdating(false); // Stop loading
  }
};

//upload images:

const [selectedAlbumId, setSelectedListingId] = useState(null);
const [uploadModalOpen, setUploadModalOpen] = useState(false);
const [files, setFiles] = useState([]);

/*const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };*/

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
  
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
  
      const compressedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          try {
            const compressed = await imageCompression(file, options);
            return compressed;
          } catch (err) {
            console.error(`Compression failed for ${file.name}:`, err);
            toast?.error?.(`Failed to compress ${file.name}`);
            return null;
          }
        })
      );
  
      const validCompressedFiles = compressedFiles.filter(Boolean);
  
      setFiles(validCompressedFiles); // <-- your state to hold compressed images
  
      console.log("Compressed files:", validCompressedFiles);
    } catch (err) {
      console.error("Image compression error:", err);
      toast?.error?.("Failed to compress images.");
    }
  };

  
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async () => {
    

      if (!selectedAlbumId) {
        toast.error("Please select a album first", { position: "top-right", autoClose: 3000 });
        return;
      }
    
      if (files.length === 0) {
        toast.error("Please select at least one image", { position: "top-right", autoClose: 3000 });
        return;
      }
  
    setIsUploading(true); // Disable button
  
    try {
      const targetKB = 100;
      const compressedFinal = [];
  
      for (const file of files) {
        let quality = 0.9;
        let compressedFile = file;
  
        while (true) {
          const options = {
            maxSizeMB: targetKB / 1024,
            initialQuality: quality,
            useWebWorker: true,
          };
  
          compressedFile = await imageCompression(file, options);
  
          if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
          quality -= 0.1;
        }
  
        compressedFinal.push(compressedFile);
      }
  
      const formData = new FormData();
      compressedFinal.forEach((file) => formData.append("images", file));
  
      await axios.post(`${API_URL}/upload-gallery-api/${selectedAlbumId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
  
      toast.success("Images uploaded successfully", { position: "top-right", autoClose: 3000 });
      setUploadModalOpen(false);
      
      setFiles([]);
      refetch();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Upload failed", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsUploading(false); // Re-enable button
    }
  };



  
  const { data: videos, isLoadingVid, isErrorVid, refetchVideos } = useQuery({
    queryKey: ["familyVideos"],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-videos-api`, { withCredentials: true })
        .then((res) => {
          if (res.data.Status === "Success" && Array.isArray(res.data.videos)) {
            return res.data.videos;
          }
          throw new Error("Failed to fetch videos");
        }),
    staleTime: 0,
    cacheTime: Infinity,
  });
  
  const videoList = videos || [];



  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1000 * 1024 * 1024) {
        toast.error("Video too large (max 1GB)");
        return;
      }
      setVideoFile(file);
    }
  };

  const handleVideoUpload = async () => {
    if (!videoFile) {
      toast.error("Please select a video first");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("video", videoFile);

      const res = await axios.post(`${API_URL}/upload-video-api`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await queryClient.invalidateQueries(["familyVideos"]);
      
      toast.success(res.data.Message || "Video uploaded successfully");
      setVideoModalOpen(false);
      setVideoFile(null);
      
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };





  const [videoModalDelete, setVideoModalDelete] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [deleting, setDeleting] = useState(false);


  const handleDeleteClick = (video) => {
    setSelectedVideo(video);
    setVideoModalDelete(true);
    //console.log("Selected vid: ", video)
    
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedVideo) return;
    setDeleting(true);

    try {
      const res = await axios.delete(
        `${API_URL}/delete-video-api/${selectedVideo.id}`,
        { withCredentials: true }
      );

      if (res.data.Status === "Success") {
        //refetchVideos?.(); 
        
        await queryClient.invalidateQueries(["familyVideos"]);
        toast.success("Video deleted successfully");
        

      } else {
        toast.error("Failed to delete video");
      }
    } catch (error) {
      toast.error("Error deleting video");
    } finally {
      setDeleting(false);
      setVideoModalDelete(false);
    }
  };

  return (
    <div className="scrollPageContainer theme-lightx"  >
      <div className="main-wrapper">
        
         {/*start of header*/}
         {isSidebarVisible && <Sidebar />}
         <Header toggleSidebar={toggleSidebar} />
         {/*end of header */}
         {/*start of sidebar*/}
         <Sidebar show={showSidebar} toggle={toggleSidebar} />
         {/*End of sidebar*/}
         {/* start of footerbar */}
         <Footerbar toggleSidebar={toggleSidebar} />
         {/* end of footerbar */}
        <ToastContainer />

        <div className="page-wrapper">
          <div className="content container-fluid ">

          <div className="row">
                  <div className="col-md-12">

                 

          <div className="d-flex align-items-center justify-content-between mb-3 flex-nowrap">
  <div className="d-flex align-items-center gap-2">
    <h6 className="mb-0 fw-semibold">Family Gallery</h6>
    {/*<span className="badge bg-success-subtle text-success border rounded-pill">
      {Array.isArray(album) ? album.length : 0} albums
    </span>*/}
  </div>

  <button
    onClick={() => setListUploadModalOpen(true)}
    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
  >
    <i className="bi bi-plus-lg" />
    Create Album
  </button>
</div>




<ReactModal isOpen={listUploadModalOpen}
              onRequestClose={() => setListUploadModalOpen(false)}
              className="Modal"
              overlayClassName="Overlay Overlay--blurx"
            >
              <div className="Modal__header">
                <div className="Modal__icon"><i className="bi bi-images" /></div>
                <div>
                  <h6 className="Modal__title mb-0">Create Album</h6>
                  <p className="Modal__sub mb-0">Give your album a short, clear name.</p>
                </div>
                <button type="button" className="btn-close ms-auto" onClick={() => setListUploadModalOpen(false)} />
              </div>
              <div className="Modal__body">
                <div className="mb-3">
                  <label className="form-label">Album name</label>
                  <input type="text" className="form-control" name="album_name" onChange={handleChange} />
                </div>
              </div>
              <div className="Modal__footer">
                

                <button className="btn btn-primary" 
                onClick={handleSubmitAlbum}
                disabled={isSubmitting}
                >
                  

                  {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      style={{ width: '0.8rem', height: '0.8rem' }}
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2 me-2" /> 
                    Save Album
                  </>
                )}

                </button>
              </div>
            </ReactModal>

          
{isLoading ? (
  <div className="loadingspinnerDiv">
  <div className="loadingspinner"></div>
</div>

) : isError ? (
  <div className="alert alert-danger">Error loading albums.</div>
) : album?.length > 0 ? (
  <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
    {album.map((item, index) => {
      const isOwner = parseInt(loggedInUserId) === parseInt(item.user_id);
      const hasImages = item.images && item.images.length > 0;

      return (
        <div className="col" key={index}>
       
          <div
            className={`album-tile ${hasImages ? '' : 'album-tile--empty'}`}
           

            onClick={() => {
              if (item.images && item.images.length > 0) {
                
                navigate('/gallery-images', { state: { albumId: item.album_id } });
                
              } else {
                toast.info("Add Images");
                setGallery([]);
                setSelectedIndex(null);
              }
            }} style={{ cursor: 'pointer' }}>
          


            <div className="album-media position-relative">
{hasImages ? (
<ImageWithLoader
src={`${API_URL}/images/${item.images[0].image_name}`}
alt={item.album_name}
className="object-fit-cover"
style={{ height: 200, width: '100%' }}
/>
) : (
<div
className="album-empty d-flex flex-column align-items-center justify-content-center"
style={{ height: 200 }}
>
<i className="bi bi-image fs-1 text-muted" />
<small className="text-muted">No Images</small>
</div>
)}


{hasImages && (
<div
className="album-tile__overlay position-absolute top-0 start-0 w-100 h-100"
style={{ background: 'rgba(0,0,0,0.3)', zIndex: 1 }}
/>
)}


<div
className="album-tile__caption position-absolute bottom-0 start-0 end-0 p-2 text-white"
style={{ zIndex: 2 }}
>
<div className="album-tile__title text-truncate fw-bold">
{item.album_name}
</div>
<div className="album-tile__meta small">
{hasImages
? `${item.images.length} ${item.images.length === 1 ? 'photo' : 'photos'}`
: 'Add photos'}
</div>
</div>
</div>


            {hasImages && (
              <span className="album-count badge rounded-pill">
                {item.images.length} {item.images.length === 1 ? 'photo' : 'photos'}
              </span>
            )}

            {isOwner && (
              <div className="album-tile__dots" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown">
                  <button
                    className="btn"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Album actions"
                    onClick={(e) => e.stopPropagation()}   
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </button>
                  <ul 
className="dropdown-menu dropdown-menu-end dropdown-menu-compact"
onClick={(e) => e.stopPropagation()}
>
<li>
<button
className="dropdown-item"
onClick={() => {
setSelectedListingId(item.album_id);
setUploadModalOpen(true);
}}
>
<i className="bi bi-upload me-2" /> Add photos
</button>
</li>
<li>
<button
className="dropdown-item"
onClick={() => {
setEditedAlbum({ id: item.album_id, name: item.album_name });
setEditModalOpen(true);
}}
>
<i className="bi bi-pencil-square me-2" /> Rename / Delete
</button>
</li>
</ul>

                </div>
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
) : (
  <div className="text-center py-5">
    <div className="mb-3">
      <i className="bi bi-images fs-1 text-muted" />
    </div>
    <h5 className="mb-1">No albums yet</h5>
    <p className="text-muted mb-3">
      Create your first album and start adding memories.
    </p>
    <button
      className="btn btn-primary"
      onClick={() => setListUploadModalOpen(true)}
    >
      <i className="bi bi-plus-sm me-2" /> Create Album
    </button>
  </div>
)}




<ReactModal
              isOpen={uploadModalOpen}
              onRequestClose={() => setUploadModalOpen(false)}
              className="Modal Modal--lg"
              overlayClassName="Overlay Overlay--blurx"
            >
              <div className="Modal__header">
                <div className="Modal__icon"><i className="bi bi-cloud-arrow-up" /></div>
                <div>
                  <h6 className="Modal__title mb-0">Upload images</h6>
                  
                </div>
                <button type="button" className="btn-close ms-auto" onClick={() => setUploadModalOpen(false)} />
              </div>

              <div className="Modal__body">
                <div >
                  <label htmlFor="imageUploadEdit" className="btn btn-outline-success d-inline-flex align-items-center gap-2">
                    <i className="bi bi-upload" /> Select images
                  </label>
                  <input id="imageUploadEdit" type="file" multiple accept="image/*" onChange={handleFileChange} className="d-none" />
                </div>

                {files.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong className="small mb-0">{files.length} selected</strong>
                      <button className="btn btn-sm btn-link text-danger p-0" onClick={() => setFiles([])}>Clear all</button>
                    </div>
                    <div className="row g-2">
                      {files.map((file, idx) => (
                        <div className="col-4 col-md-3 col-lg-2" key={idx}>
                          <div className="ratio ratio-1x1 rounded overflow-hidden border">
                            <img src={URL.createObjectURL(file)} alt={`preview-${idx}`} className="w-100 h-100 object-fit-cover" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="Modal__footer">
                
                <button className="btn btn-primary" onClick={handleImageUpload} disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" /> Uploading…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-arrow-up me-2" /> Upload
                    </>
                  )}
                </button>
              </div>
            </ReactModal>




{isEditModalOpen && (

  <ReactModal
      isOpen={isEditModalOpen}
      onRequestClose={() => setEditModalOpen(false)}
      className="Modal"
      overlayClassName="Overlay Overlay--blurx"
    >
              <div className="Modal__header">
                <div className="Modal__icon"><i className="bi bi-gear" /></div>
                <div>
                  <h6 className="Modal__title mb-0">Album settings</h6>
                  <p className="Modal__sub mb-0">Rename or delete this album.</p>
                </div>
                <button type="button" className="btn-close ms-auto" onClick={() => setEditModalOpen(false)} />
              </div>
              <div className="Modal__body">
                <label className="form-label">Album name</label>
                <input
                  type="text"
                  value={editedAlbum.name}
                  onChange={(e) => setEditedAlbum((p) => ({ ...p, name: e.target.value }))}
                  className="form-control"
                  placeholder="Enter new album name"
                />
              </div>
              <div className="Modal__footer d-flex justify-content-between">
                <button className="btn btn-outline-danger" onClick={handleDeleteAlbum} disabled={isUpdating}>
                  {isUpdating ? 'Deleting…' : <><i className="bi bi-trash me-2" />Delete</>}
                </button>
                <button className="btn btn-primary" onClick={handleUpdateAlbum} disabled={isUpdating}>
                  {isUpdating ? 'Saving…' : <><i className="bi bi-check2 me-2" />Save</>}
                </button>
              </div>
            </ReactModal>


)}




</div>
            </div>

         
        </div> 

      </div>
      

            <div className="row end-page-space"></div>
          </div>
        </div>
      
  );
}

export default Gallery;
